import { useMemo, useState } from 'react';
import {
  useActiveSpeakerId,
  useLocalSessionId,
  useParticipantIds,
  useScreenShare,
} from '@daily-co/daily-react';

import type { CallLayout, ManualLayoutChoice } from '../types';

/**
 * Synthesized tile id suffix used to surface a screen share as a sidebar/PiP
 * tile. A screen share is not a separate participant id — it shares the
 * underlying participant's `session_id`. We mint a derived id
 * (`${sessionId}:screen`) so the screen tile can coexist with the same
 * participant's camera tile in a sidebar list without colliding on React keys.
 *
 * The suffix is the same for local and remote shares; consumers derive
 * local-vs-remote at render time by comparing the inner session id to
 * `localSessionId`.
 *
 * Discrimination via `endsWith(':screen')` is safe because Daily session ids
 * are UUIDs — they will never legitimately end with `:screen`. If Daily ever
 * changes that contract, the discriminant in `isScreenTileId` and the slice
 * math in `sessionIdFromTileId` are the single point of change.
 */
export const SCREEN_TILE_SUFFIX = ':screen';

export const makeScreenTileId = (sessionId: string): string =>
  `${sessionId}${SCREEN_TILE_SUFFIX}`;

export const isScreenTileId = (tileId: string): boolean =>
  tileId.endsWith(SCREEN_TILE_SUFFIX);

export const sessionIdFromTileId = (tileId: string): string =>
  isScreenTileId(tileId)
    ? tileId.slice(0, -SCREEN_TILE_SUFFIX.length)
    : tileId;

export interface CallLayoutState {
  activeLayout: CallLayout;
  manualLayoutChoice: ManualLayoutChoice;
  setManualLayoutChoice: (choice: ManualLayoutChoice) => void;
  showLayoutToggle: boolean;
  /** For speaker view main stage (null in grid/spotlight) */
  mainStageSessionId: string | null;
  /**
   * Tile ids to render in the sidebar (speaker view) or grid. May contain
   * synthesized screen-share tile ids — use {@link isScreenTileId} to
   * distinguish them from real participant session ids.
   */
  sidebarParticipantIds: string[];
  /** Whether a remote screen share is active */
  hasRemoteScreenShare: boolean;
  /** Whether the local participant is sharing their screen */
  hasLocalScreenShare: boolean;
  /** Session ID of the remote screen sharer that owns the main stage (for rendering screenVideo) */
  screenShareSessionId: string | null;
  /** Local session id when sharing, else null — drives the synthesized local-screen tile. */
  localScreenShareSessionId: string | null;
  activeSpeakerId: string | null;
  localSessionId: string;
}

export function useCallLayout(hideSelfView = false): CallLayoutState {
  const allParticipantIds = useParticipantIds({ sort: 'joined_at' });
  const rawActiveSpeakerId = useActiveSpeakerId({ ignoreLocal: true });
  const { screens } = useScreenShare();
  const localSessionId = useLocalSessionId() ?? '';
  const [manualLayoutChoice, setManualLayoutChoice] = useState<ManualLayoutChoice>('speaker');

  const remoteParticipantIds = useMemo(
    () => allParticipantIds.filter((id) => id !== localSessionId),
    [allParticipantIds, localSessionId],
  );

  const remoteScreens = useMemo(() => screens.filter((s) => !s.local), [screens]);
  const localScreen = useMemo(() => screens.find((s) => s.local) ?? null, [screens]);

  const hasRemoteScreenShare = remoteScreens.length > 0;
  const hasLocalScreenShare = localScreen !== null;

  const screenShareSessionId = remoteScreens[0]?.session_id ?? null;
  const localScreenShareSessionId = localScreen?.session_id ?? null;

  // Synthesized screen-tile ids for remote shares *beyond* the one occupying the
  // main stage. Encoded as `${sessionId}:screen` so they don't collide with the
  // sharer's camera tile (keyed on `session_id` alone) when both appear in the
  // sidebar.
  const extraRemoteScreenTileIds = useMemo(
    () => remoteScreens.slice(1).map((s) => makeScreenTileId(s.session_id)),
    [remoteScreens],
  );

  const localScreenTileId = useMemo(
    () =>
      hasLocalScreenShare && localScreenShareSessionId
        ? makeScreenTileId(localScreenShareSessionId)
        : null,
    [hasLocalScreenShare, localScreenShareSessionId],
  );

  const activeSpeakerId =
    rawActiveSpeakerId && remoteParticipantIds.includes(rawActiveSpeakerId)
      ? rawActiveSpeakerId
      : remoteParticipantIds[0] ?? null;

  const totalParticipants = allParticipantIds.length;

  const activeLayout: CallLayout = useMemo(() => {
    // Rule 1: Remote screen share → force speaker view so viewers see the shared content large.
    //         Local screen share does NOT force a layout change — provider keeps whatever
    //         layout was active. The synthesized local-screen tile rides along instead.
    if (hasRemoteScreenShare) return 'speaker';
    // Rule 2: ≤2 participants → spotlight
    if (totalParticipants <= 2) return 'spotlight';
    // Rule 3: 3+ participants → user choice
    return manualLayoutChoice;
  }, [hasRemoteScreenShare, totalParticipants, manualLayoutChoice]);

  const showLayoutToggle = !hasRemoteScreenShare && totalParticipants >= 3;

  const { mainStageSessionId, sidebarParticipantIds } = useMemo(() => {
    const visibleIds = hideSelfView
      ? allParticipantIds.filter((id) => id !== localSessionId)
      : allParticipantIds;

    // hideSelfView controls the camera self-view, not the screen-share preview —
    // two different intents (your face vs. what you're transmitting).
    const appendLocalScreenTile = (ids: string[]): string[] =>
      localScreenTileId ? [...ids, localScreenTileId] : ids;

    if (activeLayout === 'grid') {
      return {
        mainStageSessionId: null,
        sidebarParticipantIds: appendLocalScreenTile(visibleIds),
      };
    }

    if (activeLayout === 'speaker') {
      if (hasRemoteScreenShare && screenShareSessionId) {
        // Remote screen on main stage; sidebar = all participant cameras +
        // synthesized screen tiles for any extra remote shares + local screen tile.
        // Extra remote shares are synthesized so they don't key-collide with the
        // sharer's camera tile already in the participant list.
        return {
          mainStageSessionId: screenShareSessionId,
          sidebarParticipantIds: appendLocalScreenTile([
            ...visibleIds,
            ...extraRemoteScreenTileIds,
          ]),
        };
      }

      // No remote screen share: main stage = active speaker, sidebar = everyone else + local screen if sharing.
      return {
        mainStageSessionId: activeSpeakerId,
        sidebarParticipantIds: appendLocalScreenTile(
          visibleIds.filter((id) => id !== activeSpeakerId),
        ),
      };
    }

    // Spotlight: main stage / sidebar are unused; spotlight surfaces the local-screen
    // preview as a stacked PiP thumbnail next to the camera self-view.
    return {
      mainStageSessionId: null,
      sidebarParticipantIds: [],
    };
  }, [
    activeLayout,
    hasRemoteScreenShare,
    screenShareSessionId,
    activeSpeakerId,
    allParticipantIds,
    extraRemoteScreenTileIds,
    hideSelfView,
    localScreenTileId,
    localSessionId,
  ]);

  return {
    activeLayout,
    manualLayoutChoice,
    setManualLayoutChoice,
    showLayoutToggle,
    mainStageSessionId,
    sidebarParticipantIds,
    hasRemoteScreenShare,
    hasLocalScreenShare,
    screenShareSessionId,
    localScreenShareSessionId,
    activeSpeakerId,
    localSessionId,
  };
}
